<template>
  <div class="segment-builder" ref="segmentBuilderRef">
    <div class="all-elements question-groups">
      <div class="fix-elements">
        <simplebar data-simplebar-auto-hide="false">
          <draggable
            class="list-group fix"
            v-bind="dragFixOptions"
            v-model="fixList"
            item-key="id"
            @start="dragAllElementStart"
            @end="dragAllElementEnd"
            :clone="cloneGroupElement"
            :disabled="disabledDraggable"
          >
            <template #item="{ element }">
              <div
                :key="element.id"
                :class="['list-group-item', element.type]"
              >
                <i v-if="element.type === 'group'" class="sherpa-icon icon-copy item-icon"></i>
                <div class="question-description">
                  <span class="name-uppercased">{{ getLang(element.type) }}</span>
                </div>
              </div>
            </template>
          </draggable>
        </simplebar>
      </div>
      <div class="question-elements">
        <div class="searcher-input">
          <input v-model="questionSearcher" @input="doQuestionsSearch()" />
          <transition name="fade" mode="out-in">
            <i v-if="questionSearcher.trim() === ''" class="sherpa-icon icon-buscar"></i>
            <i v-else class="sherpa-icon icon-cerrar" @click="questionSearcherReset()"></i>
          </transition>
          <transition name="fade" mode="out-in">
            <div class="searcher-count-results">{{ questionSearcher.trim() ? `${questionSearcherCount}/${questionSearcherTotal}` : questionSearcherTotal }}</div>
          </transition>
        </div>
        <simplebar data-simplebar-auto-hide="false">
          <div class="question-groups-list">
            <div v-for="group in questionGroups" :key="group.name" class="question-category">
              <div :class="['category-toggle', { 'empty': group.items.length === 0 }]" @click="group.items.length > 0 && toggleCategory(group.name)">
                <span>{{ group.name }}</span>
                <span class="category-count">{{ group.items.length }}<template v-if="questionSearcher.trim()"> / {{ group.total }}</template></span>
                <i :class="['fas', isCategoryOpen(group.name) ? 'fa-chevron-up' : 'fa-chevron-down']"></i>
              </div>
              <draggable
                v-show="isCategoryOpen(group.name) && group.items.length > 0"
                class="list-group"
                v-bind="dragQuestionOptions"
                v-model="group.items"
                item-key="id"
                @start="dragAllElementStart"
                @end="dragAllElementEnd"
                @change="doQuestionsSearch"
                :clone="cloneQuestionElement"
                :disabled="disabledDraggable"
              >
                <template #item="{ element }">
                  <div
                    :key="element.id"
                    :class="['list-group-item', element.type]"
                  >
                    <question-item v-if="containerRef && (element.type === 'question' || element.type === 'indicator')"
                      :element="element"
                      :getTypeName="getTypeName"
                      :getLang="getLang"
                      :highlightTerm="questionSearcher.trim()"
                    />
                  </div>
                </template>
              </draggable>
            </div>
          </div>
        </simplebar>
      </div>
    </div>

    <div class="selected-elements groups">
      <simplebar data-simplebar-auto-hide="false">
        <div class="list-container">
          <!-- Indicador de lista vacía -->
          <transition name="fade" mode="out-in">
            <div
              v-show="selectedElementsList.length === 0 && !dragAllElement"
              class="empty-list-indicator"
            >
              <span class="description">{{ getLang('dropItemsHere') }}</span>
              <!-- <i class="sherpa-icon icon-derecha"></i> -->
            </div>
          </transition>
          <!-- Lista de elementos seleccionados -->
          <draggable
            :class="['list-group', 'selected-group', {'dragging-element' : draggingElement}]"
            v-model="selectedElementsList"
            item-key="id"
            v-bind="dragSelectedOptions"
            :disabled="disabledDraggable"
            @start="dragSelectedElementStart"
            @end="dragSelectedElementEnd"
            @change="elementChanged"
          >
            <template #item="{ element, index }">
              <div
                :key="element.id"
                :class="['list-group-item', element.type, {'with-children': element.children && element.children.length > 1}]"
              >
                <group-item v-if="containerRef && element && element.type === 'group'"
                  :key="'group_' + element.id"
                  :element="element"
                  :parent="null"
                  :parentChildren="selectedElementsList"
                  :getTypeName="getTypeName"
                  :getLang="getLang"
                  :dragAllElement="dragAllElement"
                  :dragSelectedElementQuestion="dragSelectedElementQuestion"
                  :dragSelectedElementGroupId="dragSelectedElementGroupId"
                  :parentGroupIds="[]"
                  :dragSelectedExternalElementGroup="isExternalGroupDragged(element, dragSelectedElementGroupId, null)"
                  :dragSelectedOptions="dragSelectedOptions"
                  :selected="true"
                  :deleteElementFromList="deleteElementFromList"
                  :currentLanguage="currentLanguage"
                  :formBuilderContainer="containerRef"
                  :getElementFromSelectedList="getElementFromSelectedList"
                  :index="index"
                  :domainEndpoint="domainEndpoint"
                  :segmentId="apiDefinitionId"
                  :getRowsNumber="getRowsNumber"
                  :formatNumber="formatNumber"
                  :changeJoinValue="changeJoinValue"
                  @dragSelectedElementStart="dragSelectedChildElementStart"
                  @dragSelectedElementEnd="dragSelectedChildElementEnd"
                  @deleteElementCall="deleteElementCall"
                />
                <question-item v-if="containerRef && element && (element.type === 'question' || element.type === 'indicator')"
                  :key="'question_' + element.id"
                  :element="element"
                  :parent="null"
                  :parentChildren="selectedElementsList"
                  :getTypeName="getTypeName"
                  :getLang="getLang"
                  :selected="true"
                  :index="index"
                  :domainEndpoint="domainEndpoint"
                  :currentLanguage="currentLanguage"
                  :segmentId="apiDefinitionId"
                  :getRowsNumber="getRowsNumber"
                  :formatNumber="formatNumber"
                  :changeJoinValue="changeJoinValue"
                  @deleteElementCall="deleteElementCall"
                />
              </div>
            </template>
          </draggable>
        </div>
      </simplebar>
    </div>

    <div id="type_actions" :class="{'show': allDomainLoaded}">
      <div class="normal-buttons">
        <div class="buttons w3-twothird" key="buttons">
          <!-- Update buttons -->
          <transition name="fade" mode="out-in">
            <button v-if="apiDefinitionId && type.actions.update" v-on:click="callUpdate()" class="w3-btn w3-round-large update-and-stay" :disabled="validating">
              <transition name="fade" mode="out-in">
                <span v-if="!validating"><i class="far fa-save"></i> {{ getLang('saveAndStay') }}</span>
                <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' />
              </transition>
            </button>
          </transition>
          <transition name="fade-slide-right" mode="out-in">
            <div v-if="totalRows !== null" class="total-rows">
              <span class="total-label" key="total-label"><i :class="['fas', 'fa-sort-numeric-down', {'loading': totalRowsLoading}]"></i> {{ getLang('totalRows') }}</span>
              <transition name="fade-slide-right" mode="out-in">
                <span v-if="!totalRowsLoading" class="total" key="total">{{ formatNumber(totalRows) }}</span>
                <spinner-loading class="spinner-loading" v-else :line-size='2' line-fg-color='#5dbecd' key="total-loading" />
              </transition>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <!-- Popup -->
    <transition name="fade" mode="out-in">
      <builder-popup
        v-show="containerRef && popupVisibility"
        :popupContainer="containerRef"
        :isVisible="popupVisibility"
        ref="saveMessage"
        @popupVisibility="changePopupVisibility"
      >
        <div class="popup-container translation">
          <div class="title">
            <i class="sherpa-icon icon-formulario"></i>
            <span>{{ getLang('saveResults') }}</span>
          </div>
          <div class="popup-content operation-results">
            <div v-if="operationResult" class="operation-ok">
              <i class="sherpa-icon icon-ico_ok"></i>
              <span class="operation-description">{{ getLang('saveResultsOk') }}</span>
            </div>
            <div v-else class="operation-ko">
              <i class="sherpa-icon icon-ico_error"></i>
              <span class="operation-description">{{ getLang('saveResultsKo') }}</span>
              <div v-for="(error, index) in operationErrors" class="operation-errors" :key="index">
                <div class="error-id">{{ error.id }}</div>
                <div class="error-text" v-html="error.textError" />
              </div>
            </div>
          </div>
        </div>
      </builder-popup>
    </transition>

    <!-- Chat IA -->
    <transition name="chat-panel-slide">
      <div v-if="chatOpen" class="ai-chat-panel">
        <div class="chat-header">
          <i class="fas fa-robot"></i>
          <span>{{ getLang('chatTitle') }} <small style="opacity:0.6;font-weight:400;font-size:11px">(beta)</small></span>
          <i class="fas fa-times chat-close" @click="chatOpen = false"></i>
        </div>
        <div class="chat-messages" ref="chatMessagesRef">
          <div v-if="chatMessages.length === 0" class="chat-empty">
            <i class="fas fa-comment-dots"></i>
            <p>{{ getLang('chatEmptyDesc') }}</p>
            <p class="chat-example"><em>{{ getLang('chatEmptyExample') }}</em></p>
          </div>
          <div v-for="(msg, i) in chatMessages" :key="i" :class="['chat-message', msg.role]">
            <div class="message-bubble">
              <span v-html="msg.text"></span>
            </div>
          </div>
          <div v-if="chatLoading" class="chat-message assistant">
            <div class="message-bubble loading">
              <span class="dot"></span><span class="dot"></span><span class="dot"></span>
            </div>
          </div>
        </div>
        <div class="chat-input-area">
          <textarea
            ref="chatInputRef"
            v-model="chatInput"
            :placeholder="getLang('chatPlaceholder')"
            :disabled="chatLoading"
            @keydown.enter.exact.prevent="sendChatMessage"
          ></textarea>
          <button :disabled="chatLoading || chatInput.trim() === ''" @click="sendChatMessage">
            <i :class="chatLoading ? 'fas fa-spinner fa-spin' : 'fas fa-paper-plane'"></i>
          </button>
        </div>
      </div>
    </transition>

    <transition name="fab-bounce">
      <button v-if="!chatOpen" class="ai-chat-fab" @click="openChat" :title="getLang('chatTitle')">
        <i class="fas fa-comments"></i>
      </button>
    </transition>
  </div>
</template>

<script>
import * as appTypes from '@/store/actions/app'
import draggable from 'vuedraggable'
import simplebar from 'simplebar-vue'
import languages from '@/language/languages'
import questionItem from './questionItem'
import groupItem from './groupItem'
import * as api from '../definitionLoader/utils/definitionApi'
import Configuration from '@/configuration'
import moment from 'moment'
import builderPopup from '../builderPopup'
import debounce from 'lodash/debounce'

const initialFixList = [
  {
    id: 'group',
    type: 'group',
    subtype: null,
    name: '',
    children: [],
    active: true,
    joinValue: 'and'
  }
]

export default {
  name: 'segment-builder',
  components: {
    draggable,
    simplebar,
    questionItem,
    groupItem,
    builderPopup
  },
  props: [
    'content',
    'type',
    'apiDefinitionId'
  ],
  data () {
    return {
      fixList: null,
      dragAllElement: false,
      dragSelectedElementQuestion: false,
      dragSelectedElementGroup: false,
      dragSelectedElementGroupId: null,
      disabledDraggable: false,
      showList: [],
      questionsList: null,
      selectedElementsList: null,
      questionSearcher: '',
      questionSearcherCount: 0,
      questionSearcherTotal: 0,
      refsReady: false,
      currentLanguage: null,
      dragStartY: null,
      domainEndpoint: null,
      totalRowsEndpoint: null,
      chatEndpoint: null,
      validating: false,
      popupVisibility: false,
      operationResult: false,
      operationErrors: [],
      totalRowsLoading: false,
      totalRows: null,
      openCategories: {},
      chatOpen: false,
      chatMessages: [],
      chatInput: '',
      chatLoading: false
    }
  },
  beforeMount () {
    this.builderLoaded()
    this.doQuestionsSearch()
    this.doSetLanguage()
  },
  mounted () {
    this.$nextTick(() => {
      this.refsReady = true
    })
  },
  methods: {
    builderLoaded () {
      if (this.content) {
        this.$emit('refreshDefinitionTitle', this.content.segmentName)
        this.fixList = JSON.parse(JSON.stringify(initialFixList))
        this.questionsList = JSON.parse(JSON.stringify([
          ...this.content.questionsList,
          ...(this.content.indicatorsList || [])
        ]))
        this.selectedElementsList = JSON.parse(JSON.stringify(this.content.selectedList))
        this.domainEndpoint = this.content.domainEndpoint
        this.totalRowsEndpoint = this.content.totalRowsEndpoint
        this.chatEndpoint = this.content.chatEndpoint
        this.$emit('definitionLoaded', this.content)
      }
      this.$store.dispatch(`${appTypes.APP_NAMESPACE}/${appTypes.APP_LOADING}`, false)
    },
    doSetLanguage () {
      let appLanguage = this.$ml.current.toLowerCase()
      this.currentLanguage = appLanguage
    },
    dragAllElementStart () {
      this.dragAllElement = true
    },
    dragAllElementEnd () {
      this.dragAllElement = false
    },
    cloneGroupElement (element) {
      let newElement = JSON.parse(JSON.stringify(element))
      newElement.id = `group-${Date.now()}`
      newElement.active = true
      return newElement
    },
    cloneQuestionElement (element) {
      let newElement = JSON.parse(JSON.stringify(element))
      newElement.id = `${element.id}-${Date.now()}`
      newElement.active = true
      newElement.recentlyAdded = true
      return newElement
    },
    getLang (key) {
      return languages[this.$ml.current].segmentBuilder[key]
    },
    doQuestionsSearch () {
      this.questionSearcherCount = 0
      this.questionSearcherTotal = 0
      this.showList = []
      for (var i = 0; i < this.questionsList.length; i++) {
        var question = this.questionsList[i]

        this.questionSearcherTotal += 1
        if (!this.questionSearcher || this.questionSearcher.trim() === '') {
          this.questionSearcherCount += 1
          this.showList.push(question)
        } else {
          if (
            (question.name && this.normalizeText(question.name).indexOf(this.normalizeText(this.questionSearcher.trim())) > -1) ||
            (question.indexField && this.normalizeText(question.indexField).indexOf(this.normalizeText(this.questionSearcher.trim())) > -1)
          ) {
            this.questionSearcherCount += 1
            this.showList.push(question)
          }
        }
      }
    },
    normalizeText (text) {
      return text
        .toLowerCase() // Convertir a minúsculas
        .normalize('NFD') // Descomponer caracteres acentuados
        .replace(/[\u0300-\u036f]/g, '') // Eliminar diacríticos
    },
    questionSearcherReset () {
      this.questionSearcher = ''
      this.doQuestionsSearch()
    },
    deleteElementCall (element) {
      this.deleteElementFromList(element, this.selectedElementsList)
    },
    deleteElementFromList (element, list) {
      const index = list.findIndex(el => el.id === element.id)
      if (index !== -1) {
        list.splice(index, 1)
        this.doQuestionsSearch()
      }
    },
    getName (names, language) {
      let name = names[language]
      if (!name || name === '') {
        return `<span class='empty-name'>${this.getLang('emptyName')}</span>`
      } else {
        return name
      }
    },
    getTypeName (element) {
      if (element) {
        if (element.subtype === 'domain') {
          return this.getLang('typeDomain')
        } else if (element.subtype === 'date') {
          return this.getLang('typeDate')
        } else if (element.subtype === 'number') {
          return this.getLang('typeNumber')
        } else if (element.subtype === 'text') {
          return this.getLang('typeText')
        } else {
          return '?'
        }
      } else {
        return ''
      }
    },
    dragSelectedElementStart (event) {
      this.dragStartY = event.originalEvent.clientY
      if (this.selectedElementsList[event.oldIndex] && this.selectedElementsList[event.oldIndex].type === 'question') {
        this.dragSelectedElementQuestion = true
      } else if (this.selectedElementsList[event.oldIndex] && this.selectedElementsList[event.oldIndex].type === 'group') {
        this.dragSelectedElementGroup = true
        this.dragSelectedElementGroupId = this.selectedElementsList[event.oldIndex].id
      }
    },
    dragSelectedElementEnd () {
      this.dragSelectedElementQuestion = false
      this.dragSelectedElementGroup = false
      this.dragSelectedElementGroupId = null
    },
    elementChanged () {
    },
    isExternalGroupDragged (element, selectedGroupId) {
      return this.dragSelectedElementGroup && selectedGroupId !== null && !(element && element.id === selectedGroupId)
    },
    dragSelectedChildElementStart (element, event) {
      this.dragStartY = event.originalEvent.clientY
      if (element && element.type === 'question') {
        this.dragSelectedElementQuestion = true
      } else if (element && element.type === 'group') {
        this.dragSelectedElementGroup = true
        this.dragSelectedElementGroupId = element.id
      }
    },
    dragSelectedChildElementEnd () {
      this.dragSelectedElementQuestion = false
      this.dragSelectedElementGroup = false
      this.dragSelectedElementGroupId = null
    },
    getElementFromSelectedList (id) {
      return this.getElementById(id, this.selectedElementsList)
    },
    getElementById (id, list) {
      let result = null
      if (list) {
        for (let i = 0; i < list.length && result === null; i++) {
          let element = list[i]
          if (element.id === id) {
            result = element
          } else {
            result = this.getElementById(id, element.children)
          }
        }
      }
      return result
    },
    callUpdate () {
      this.validating = true
      api.call({domainEndpoint: Configuration.value('API_SERVICES_URL') + moment().valueOf(), url: this.type.actions.update.endpoint, token: null, version: null, method: 'POST', lang: this.$ml.current.toLowerCase(), authorization: window.localStorage.getItem('user-token'), data: { id: this.apiDefinitionId, code: null, jsonData: { id: this.apiDefinitionId, appLang: this.$ml.current, selectedList: this.selectedElementsList } }})
        .then(resp => {
          if (resp.requestResult) {
            this.operationResult = true
            this.popupVisibility = true
          } else {
            this.operationErrors = resp.requestResultError.requestCause
            this.operationResult = false
            this.popupVisibility = true
          }
          this.validating = false
        })
        .catch(err => {
          console.error(err)
          this.operationResult = false
          this.operationErrors = [
            {
              id: this.getLang('errorUnknownId'),
              textError: this.getLang('errorUnknownText')
            }
          ]
          this.popupVisibility = true
          this.validating = false
        })
    },
    changePopupVisibility (visibility) {
      this.popupVisibility = visibility
    },
    isAllDomainLoaded (list) {
      const check = (items) => {
        for (const item of items) {
          if (!item.active) continue // Ignorar elementos no activos

          if (item.subtype === 'domain' && (item.operation === null || (
            item.operation.domain === null &&
            !['informed', 'not-informed'].includes(item.operation.operationValue)
          ))) {
            return false
          }

          // Si tiene hijos, comprobar recursivamente solo si están activos
          if (item.children && item.children.length > 0) {
            if (!check(item.children)) {
              return false
            }
          }
        }
        return true
      }

      return check(list)
    },
    waitForAllDomainLoaded () {
      const unwatch = this.$watch('allDomainLoaded', (newVal) => {
        if (newVal) {
          unwatch() // Deja de observar cuando sea true
          this.getTotalRows()
        }
      })
    },
    getTotalRows () {
      return this.getRowsNumber(this.selectedElementsList, this)
    },
    getRowsNumber (list, component) {
      if (!component._totalRowsSeq) component._totalRowsSeq = 0
      const seq = ++component._totalRowsSeq
      component.totalRowsLoading = true
      component.totalRows = 0
      api.call({domainEndpoint: Configuration.value('API_SERVICES_URL') + moment().valueOf(), url: this.totalRowsEndpoint, token: null, version: null, method: 'POST', lang: this.$ml.current.toLowerCase(), authorization: window.localStorage.getItem('user-token'), data: { id: this.apiDefinitionId, code: null, jsonData: { id: this.apiDefinitionId, appLang: this.$ml.current, selectedList: list } }})
        .then(resp => {
          if (seq !== component._totalRowsSeq) return
          if (resp && resp.requestSearcher && resp.requestSearcher.requestPage) {
            component.totalRows = resp.requestSearcher.requestPage.totalRows
          } else {
            component.totalRows = null
          }
          setTimeout(() => {
            if (seq === component._totalRowsSeq) component.totalRowsLoading = false
          }, 300)
        })
        .catch(err => {
          if (seq !== component._totalRowsSeq) return
          console.log(err)
          component.totalRowsLoading = false
          component.totalRows = null
        })
    },
    formatNumber (value) {
      if (!value && value !== 0) return ''
      return new Intl.NumberFormat(this.currentLanguage.replace('_', '-')).format(value)
    },
    fixFirstListElements (list) {
      if (!Array.isArray(list) || list.length === 0) return

      // Corregimos el primer elemento si es 'or' o 'and'
      const first = list[0]
      if (first.joinValue === 'or' || first.joinValue === 'and') {
        first.joinValue = ''
      }

      // A los demás, si tienen joinValue vacío, poner 'and'
      for (let i = 1; i < list.length; i++) {
        if (!list[i].joinValue) {
          list[i].joinValue = 'and'
        }
      }

      // Recorremos recursivamente los hijos (si hay)
      for (const item of list) {
        if (Array.isArray(item.children) && item.children.length > 0) {
          this.fixFirstListElements(item.children)
        }
      }
    },
    toggleCategory (name) {
      this.openCategories[name] = !this.isCategoryOpen(name)
    },
    isCategoryOpen (name) {
      return this.openCategories[name] === true
    },
    changeJoinValue (component, value, index) {
      if (index === 0 && value === 'minus' && component.element.joinValue === 'minus') {
        component.element.joinValue = ''
      } else {
        component.element.joinValue = value
      }
    },
    openChat () {
      this.chatOpen = true
      this.chatMessages.push({ role: 'assistant', text: this.buildConditionsSummary() })
      this.$nextTick(() => {
        this.scrollChatToBottom()
        if (this.$refs.chatInputRef) this.$refs.chatInputRef.focus()
      })
    },
    getOpLabel (operationValue, subtype) {
      const map = {
        // text
        'equal':            subtype === 'domain' ? 'opDomainEqualName'          : 'opTextEqualName',
        'contains':         'opTextContainsName',
        'starts-with':      'opTextStartsWithName',
        'ends-with':        'opTextEndsWithName',
        'not-equal':        subtype === 'number' ? 'opNumberNotEqualName'        : 'opTextNotEqualName',
        'not-contains':     'opTextNotContainsName',
        'on-list':          'opTextOnListName',
        'off-list':         'opTextOffListName',
        'informed':         'opTextInformedName',
        'not-informed':     'opTextNotInformedSomeName',
        // number
        'between':          'opNumberBetweenName',
        'not-between':      'opNumberNotBetweenName',
        'bigger':           'opNumberBiggerName',
        'bigger-or-equal':  'opNumberBiggerOrEqualName',
        'less':             'opNumberLessName',
        'less-or-equal':    'opNumberLessOrEqualName',
        // domain
        'contains-some':    'opDomainContainsSomeName',
        'contains-all':     'opDomainContainsAllName',
        'not-contains':     'opDomainNotContainsName',
        // date
        'today':            'opTextEqualName'
      }
      const key = map[operationValue]
      return key ? this.getLang(key) : operationValue
    },
    buildConditionsSummary () {
      const list = this.selectedElementsList
      if (!list || list.length === 0) {
        return this.getLang('chatNoConditions')
      }
      const lines = ['<b>' + (this.getLang('chatSummaryIntro') || 'Condicions actuals del segment:') + '</b>']
      const formatItem = (item, indent) => {
        const pad = '&nbsp;'.repeat(indent * 4)
        if (item.type === 'group') {
          lines.push(pad + '📁 <b>' + (item.name || 'Grup') + '</b>')
          if (item.children) item.children.forEach(c => formatItem(c, indent + 1))
        } else {
          const op = item.operation || {}
          const opLabel = this.getOpLabel(op.operationValue, item.subtype)
          let val = ''
          if (op.textListValue) val = opLabel + ': <i>' + op.textListValue + '</i>'
          else if (op.textValue) val = opLabel + ': <i>' + op.textValue + '</i>'
          else if (op.numberValue !== null && op.numberValue !== undefined && op.numberValue !== '') val = opLabel + ': <i>' + op.numberValue + '</i>'
          else if (op.dateValue) val = opLabel + ': <i>' + op.dateValue + '</i>'
          else if (op.todayDiffDays !== null && op.todayDiffDays !== undefined) val = opLabel + ': <i>avui ' + (op.todayDiffDays >= 0 ? '+' : '') + op.todayDiffDays + ' dies</i>'
          else if (op.domain && op.domain.selected && op.domain.selected.length) val = opLabel + ': <i>' + op.domain.selected.map(s => s.name).join(', ') + '</i>'
          else val = opLabel
          const join = item.joinValue ? '<span style="color:#888;font-size:11px">' + item.joinValue.toUpperCase() + '</span> ' : ''
          lines.push(pad + join + '<b>' + (item.name || item.indexField) + '</b>' + (val ? ' · ' + val : ''))
        }
      }
      list.forEach(item => formatItem(item, 0))
      return lines.join('<br>')
    },
    async sendChatMessage () {
      const text = this.chatInput.trim()
      if (!text || this.chatLoading) return
      this.chatMessages.push({ role: 'user', text })
      this.chatInput = ''
      this.chatLoading = true
      this.$nextTick(() => this.scrollChatToBottom())
      try {
        const response = await api.call({
          domainEndpoint: Configuration.value('API_SERVICES_URL') + moment().valueOf(),
          url: this.chatEndpoint,
          token: null,
          version: null,
          method: 'POST',
          lang: this.$ml.current.toLowerCase(),
          authorization: window.localStorage.getItem('user-token'),
          data: {
            id: this.apiDefinitionId,
            code: null,
            jsonData: JSON.stringify({
              userMessage: text,
              currentConditions: this.selectedElementsList,
              chatHistory: this.chatMessages.slice(0, -1) // tots els missatges menys l'últim (que és el que s'envia)
            })
          }
        })
        if (response) {
          if (response.selectedList) {
            this.selectedElementsList = response.selectedList
            this.getTotalRows()
          }
          this.chatMessages.push({ role: 'assistant', text: response.message || '✓' })
        } else {
          this.chatMessages.push({ role: 'assistant', text: this.getLang('chatErrorConditions') })
        }
      } catch (e) {
        this.chatMessages.push({ role: 'assistant', text: this.getLang('chatError') })
      } finally {
        this.chatLoading = false
        this.$nextTick(() => this.scrollChatToBottom())
      }
    },
    scrollChatToBottom () {
      const el = this.$refs.chatMessagesRef
      if (el) el.scrollTop = el.scrollHeight
    }
  },
  computed: {
    allDomainLoaded () {
      return this.isAllDomainLoaded(this.selectedElementsList)
    },
    dragFixOptions () {
      return {
        animation: 200,
        group: {
          name: 'fix',
          put: false,
          pull: 'clone'
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: false
      }
    },
    dragQuestionOptions () {
      return {
        animation: 200,
        group: {
          name: 'question',
          put: false,
          pull: 'clone'
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: false
      }
    },
    dragSelectedOptions () {
      return {
        animation: 200,
        group: {
          name: 'selected',
          put: ['fix', 'question', 'selected'],
          pull: true
        },
        disabled: false,
        ghostClass: 'ghost',
        sort: true, // Habilitamos el sort para poder reordenar
        move: (evt, originalEvent) => {
          if (evt.from === evt.to && evt.related) {
            const rect = evt.related.getBoundingClientRect()
            const cursorY = originalEvent.clientY
            const movingDown = cursorY > this.dragStartY // Detecta si el cursor está moviéndose hacia abajo

            if (movingDown) {
              // Si venimos de arriba, permitimos reordenar solo si pasamos el 75% inferior
              if (cursorY < rect.top + rect.height * 0.75) {
                return false
              }
            } else {
              // Si venimos de abajo, permitimos reordenar solo si estamos en el 25% superior
              if (cursorY > rect.top + rect.height * 0.25) {
                return false
              }
            }
          }
          return true
        }
      }
    },
    questionGroups () {
      const genericLabel = this.getLang('genericFilters')
      const searcher = this.questionSearcher.trim()
      const allGroups = {}
      const order = []
      for (const q of this.questionsList) {
        const cat = q.categoryName || genericLabel
        if (!allGroups[cat]) {
          allGroups[cat] = []
          order.push(cat)
        }
        allGroups[cat].push(q)
      }
      return order.map(cat => {
        const total = allGroups[cat].length
        const items = searcher === '' ? allGroups[cat] : allGroups[cat].filter(q =>
          (q.name && this.normalizeText(q.name).indexOf(this.normalizeText(searcher)) > -1) ||
          (q.indexField && this.normalizeText(q.indexField).indexOf(this.normalizeText(searcher)) > -1)
        )
        return { name: cat, type: allGroups[cat][0] ? allGroups[cat][0].type : null, items, total }
      })
    },
    containerRef () {
      return this.refsReady ? this.$refs['segmentBuilderRef'] : null
    },
    draggingElement () {
      return this.dragAllElement || this.dragSelectedElementQuestion || this.dragSelectedElementGroup
    },
    selectedElementsFingerprint () {
      const extractRelevantData = list => {
        if (!Array.isArray(list)) return []

        return list.map(el => ({
          joinValue: el && el.joinValue,
          operation: el && el.operation,
          active: el && el.active,
          children: extractRelevantData(el && el.children)
        }))
      }

      return JSON.stringify(extractRelevantData(this.selectedElementsList))
    }
  },
  watch: {
    selectedElementsFingerprint: {
      handler: debounce(function () {
        if (this.allDomainLoaded) {
          this.getTotalRows()
        } else {
          // Espera a que allDomainLoaded se vuelva true
          this.waitForAllDomainLoaded()
        }
        this.fixFirstListElements(this.selectedElementsList)
      }, 1000),
      immediate: true
    }
  }
}
</script>

<style lang="scss">
.segment-builder {
  position: fixed;
  left: 111px;
  right: 12px;
  bottom: 70px;
  top: 133px;
  display: flex;
  gap: 10px;
  background-color: #fafafa;

  /* Animaciones para fade */
  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-enter-active {
    transform: translateY(-15px);
  }
  .fade-enter-from,
  .fade-leave-to {
    opacity: 0;
    transform: translateY(-20px);
  }
  .fade-enter-to,
  .fade-leave-from {
    opacity: 1;
    transform: translateY(0);
  }

  /* Animaciones para fade-slide-left */
  .fade-slide-left-enter-active,
  .fade-slide-left-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-slide-left-enter-active {
    transform: translateX(-20px);
  }
  .fade-slide-left-enter-from,
  .fade-slide-left-leave-to {
    opacity: 0;
    transform: translateX(-25px);
  }
  .fade-slide-left-enter-to,
  .fade-slide-left-leave-from {
    opacity: 1;
    transform: translateX(0);
  }

  /* Animaciones para fade-slide-right */
  .fade-slide-right-enter-active,
  .fade-slide-right-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
  }
  .fade-slide-right-enter-active {
    transform: translateX(20px);
  }
  .fade-slide-right-enter-from,
  .fade-slide-right-leave-to {
    opacity: 0;
    transform: translateX(25px);
  }
  .fade-slide-right-enter-to,
  .fade-slide-right-leave-from {
    opacity: 1;
    transform: translateX(0);
  }

  .all-elements {
    flex-basis: 18%;
    max-width: 18%;
    min-width: 18%;
    display: flex;
    flex-direction: column;
    background-color: #f5f5f5;
    border: 1px dotted #999;
    overflow: hidden;
    position: relative;
    z-index: 0;

    .fix-elements {
      margin-top: 8px;
    }

    .question-elements {
      position: absolute;
      top: 90px;
      left: 0;
      bottom: 0;
      right: 0;

      [data-simplebar] {
        height: 93%;
        margin-top: 8px;
      }

      .question-item {
        display: flex;
        gap: 2px;
      }

      .question-groups-list {
        display: flex;
        flex-direction: column;

        .question-category {
          .category-toggle {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin: 4px 8px 5px;
            padding: 5px 10px;
            border-radius: 4px;
            cursor: pointer;
            font-family: 'Source Sans Pro';
            font-size: 13px;
            font-weight: 600;
            user-select: none;
            transition: background-color 0.15s ease;

            i {
              font-size: 11px;
              width: auto;
            }

            .category-count {
              margin-left: auto;
              margin-right: 6px;
              font-size: 11px;
              font-weight: 400;
              opacity: 0.7;
            }

            background-color: #f0f5ff;
            border: 1px solid #c8d8f0;
            color: #2a5fa8;
            &:hover { background-color: #e4eeff; }
            i { color: #2a5fa8; }

            &.empty {
              opacity: 0.4;
              cursor: default;
              pointer-events: none;
            }
          }
        }
      }
    }
  }

  .selected-elements {
    flex-grow: 1;    // Ocupa el resto del espacio disponible
    display: flex;
    flex-direction: column;
    background-color: #e4fffe;
    border: 1px dotted #999;
    padding-top: 8px;

    [data-simplebar] {
      height: 100%;
    }

    .list-group {
      & > span {
        display: block;
        transition: border 0.2s ease;
        border: 1.5px dashed transparent;
        position: absolute;
        top: 8px;
        left: 8px;
        right: 8px;
        bottom: 8px;
        transition: all 0.2s ease;
      }
      &.dragging-element {
        & > span {
          border: 1.5px dashed #d42f12cb;
        }
      }
      &.selected-group {
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        overflow-y: auto;
      }
    }

    .list-group-item {
      .question-description {
        padding-right: 40px;
      }
      &.group {
        padding: 12px 13px 18px;
        position: relative;
      }
    }

    .language-text {
      padding-right: 60px;
    }

    .list-container {
      height: 100%;
      display: flex;
      flex-direction: column;
      gap: 10px;
      position: absolute;
      right: 0;
      left: 0;
    }

    .empty-list-indicator {
      text-align: center;
      color: #888;
      font-style: italic;
      position: absolute;
      right: 8px;
      left: 8px;
      bottom: 8px;
      top: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      border: 1px dashed #999;
      background-color: #f6f6f6;
      pointer-events: none;
      font-size: 15px;
      font-weight: 400;
      font-family: 'Source Sans Pro';

      span {
        display: block;
      }

      i {
        display: block;
        transform: rotate(90deg);
        font-size: 20px;
        margin: 0 auto;
        display: block;
      }
    }
  }

  .list-group {
    display: flex;
    flex-direction: column;
    gap: 0;
    min-height: 80px;
    margin: 0;
    padding: 0 10px;

    &.fix {
      min-height: initial;
    }
  }

  .list-group-item {
    cursor: move;
    padding: 0;
    background-color: #fff;
    border: 1px solid #ddd;

    border-radius: 4px;
    margin-bottom: 5px;
    font-family: 'Source Sans Pro';
    font-size: 16px;
    transition: all 0.2s ease;
    position: relative;

    &:hover {
      border: 1px solid #999;
    }

    &.group {
      background-color: #e8e8e8;
      padding: 26px 13px 24px;

      .question-description {
        .name-uppercased {
          text-transform: uppercase;
          font-weight: 600;
          padding-left: 6px;
        }
      }

      .empty-list-indicator {
        position: relative;
        height: 117.5px;
        right: 0;
        left: 0;
        bottom: 0;

        i {
          margin: 5px auto;
        }
      }
    }

    i {
      font-size: 18px;
      margin-right: 0;
      color: #7f7f7f;
      width: 18px;
      vertical-align: top;

      &.item-icon {
        position: absolute;
      }
    }

    .question-description {
      display: inline-block;
      line-height: 18px;
      padding-left: 22px;
      width: 100%;

      .description {
        font-size: 14px;
        color: #707070;
        padding: 3px 2px 3px 5px;
        display: block;
        word-break: break-all;

        &.type {
          font-size: 13px;
          color: #9f9f9f;
          display: inline-block;

          .type-selected {
            font-weight: 700;
            margin-left: 2px;
          }
        }
      }
    }

    .edition-element {
      position: absolute;
      text-align: right;

      i {
        font-size: 19px;
        color: #d42f12cb;
        transition: color 0.3s ease;
        cursor: pointer;
        margin-left: 3px;
        vertical-align: text-top !important;

        &:hover {
          color: #d42f12;
        }
      }
    }

    .group-item {
      .total-rows {
        .spinner-loading {
          display: inline-block;

          .vue-simple-spinner {
            width: 14px !important;
            height: 14px !important;
          }
        }
      }
    }
  }

  .ghost {
    opacity: 0.8;
    background-color: #c8ebfb;
  }

  .searcher-input {
    padding: 10px 10px 0;

    input {
      border: 0;
      outline: none;
      font-family: 'Source Sans Pro';
      padding: 5px 65px 5px 10px;
      border: 1px solid #ddd;
      width: 100%;
      transition: border 0.2s ease;

      &:hover,
      &:focus {
        border: 1px solid #999;
      }
    }

    i {
      position: absolute;
      right: 20px;
      top: 18px;
      color: #d42e12;

      &.icon-cerrar {
        cursor: pointer;
      }
    }

    .searcher-count-results {
      position: absolute;
      right: 41px;
      top: 21px;
      font-size: 10px;
      font-style: italic;
      color: #9f9f9f;
    }
  }

  .builder-select {
    display: block;
    text-align: right;
    padding-right: 2px;

    .multiselect {
      height: 26px !important;
      min-height: 26px !important;

      .multiselect__select,
      .multiselect__tags {
        height: 26px !important;
        min-height: 26px !important;
      }

      .multiselect__select {
        padding: 0;
        width: 30px;
        height: 25px;
        min-height: 25px;
        line-height: 15px;

        &:before {
          color: #d42e12;
          border-color: #d42e12 transparent transparent transparent;
        }
      }

      .multiselect__tags {
        background-color: #e4fffe;
        border: 1px solid #ddd;
        padding: 3px 30px 0 8px;
        border-radius: 0;
        min-width: 200px;

        .multiselect__single {
          padding: 0;
          background-color: transparent;
          margin-bottom: 0;
          font-size: 13px;
          color: #333;
          font-weight: 600;
          font-family: 'Source Sans Pro';
        }

        .multiselect__placeholder {
          display: none;
        }
      }

      .multiselect__content-wrapper {
        position: absolute !important;
        z-index: 1000 !important;
        border: 1px solid #ddd;
        width: 200px;
        right: 0;
        left: 0;

        .multiselect__option {
          font-size: 13px;
          padding: 6px 8px;
          min-height: 26px !important;
        }

        .multiselect__option--highlight,
        .multiselect__option--selected {
          background-color: #e4fffe;
          color: #333;
        }
        .multiselect__option--selected {
          font-weight: 600;
        }
      }
    }

    &.action {
      .multiselect__content-wrapper {
        border-radius: 0 !important;
        top: -41px !important;
        left: -1px;
        right: -1px;
      }
    }
  }

  .popup {
    overflow: auto;

    .popup-container {
      min-width: 1000px;
      font-family: 'Source Sans Pro';
      text-transform: none;

      .title {
        color: #666;
        padding: 8px 11px 6px;
        font-size: 15px;
        font-weight: 500;
        border-bottom: 1px solid #999;
        background-color: #e8e8ee;
        line-height: 15px;

        i {
          font-size: 20px;
          color: #666;
          margin-right: 5px;
          vertical-align: middle;
        }
      }

      .popup-content {
        min-width: 1000px;

        &.operation-results {
          padding: 10px 15px;

          .operation-description {
            font-size: 14px;
            font-family: 'Lucida Sans', 'Lucida Sans Regular', 'Lucida Grande', 'Lucida Sans Unicode', Geneva, Verdana, sans-serif;
            font-weight: 300;
          }

          i {
            vertical-align: text-top;
            font-size: 16px;
            margin-right: 10px;

            &.icon-ico_ok {
              color: #4ab800c5;
            }

            &.icon-ico_error {
              color: #e30000;
            }
          }

          .operation-errors {
            width: 100%;
            padding: 5px 0 4px 30px;

            div {
              display: block;
            }

            .error-id {
              color: #e30000;
              font-weight: 600;
            }

            .error-text {
              color: #454545;
            }
          }
        }

        .translation-language {
          display: flex;
          transition: all 0.2s ease;
          border: 1px dotted #aaa;

          &:hover {
            cursor: pointer;

            .language-key {
              color: #000;
              background-color: #8ab6f4be;
            }

            .name {
              background-color: #fff;
            }
          }

          .language-key {
            padding: 7px 11px 7px;
            min-width: 75px;
            font-size: 15px;
            font-weight: 500;
            background-color: transparent;
            border-right: 1px dotted #aaa;
            color:#076cfabe;
            display: flex;
            justify-content: center;
            align-items: center;
            transition: all 0.2s ease;
          }

          .name {
            width: 100%;
            display: inline-block;
            transition: all 0.2s ease;
            background-color: #f5f5f5;
            position: relative;

            > span {
              padding: 8px 15px 7px;
              display: inline-block;
              user-select: text;
              cursor: text;

              > p {
                margin: 0;
              }
            }

            .quillWrapper {
              border: 0;

              .ql-toolbar {
                background-color: #e8e9f7;

                &.ql-snow {
                  border: 0;
                }
              }
            }

            .buttons-container {
              position: absolute;
              top: 11px;
              right: 10px;

              .icon-borrar,
              .icon-translate {
                color: #d9614b;
                transition: all 0.2s ease;

                &:hover {
                  color: #d42f12;
                }
              }

              .spinner-loading {
                display: inline-block;
              }

              &.selected {
                top: initial;
                bottom: 5px;
              }
            }
          }

          &.selected {
            .language-key {
              color: #000;
              font-weight: 600;
              background-color: #8ab6f4be;
            }

            .name {
              background-color: #fff;
            }
          }
        }
      }
    }
  }

  .empty-name {
    font-size: 16px;
    font-style: italic;
    color: #afafaf;
    font-weight: 300;
    text-transform: none;
  }

  .join-values {
    padding: 5px 5px;
    background-color: #f8f8ec;
    text-align: center;
    border-bottom: 1px dotted #999;
    border-right: 1px dotted #999;
    font-size: 16px;
    width: 225px;
    position: absolute;
    top: 1px;
    left: 1px;
    z-index: 10;

    &.not-selected {
      filter: grayscale(100%);
      opacity: 0.6;
      pointer-events: none;
    }

    .operation-desc {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      font-size: 12px;
      color: #777;
      font-family: 'Lucida Sans', 'Lucida Sans Regular', 'Lucida Grande', 'Lucida Sans Unicode', Geneva, Verdana, sans-serif;
      margin-right: 5px;
    }

    .join-button {
      width: 60px;
      height: 24px;
      border-radius: 6px;
      border: 1.5px solid #aaa;
      display: inline-flex;
      justify-content: center;
      align-items: center;
      transition: all 0.2s ease;
      text-transform: uppercase;
      opacity: 0.3;
      cursor: pointer;
      font-weight: 400;

      &.and {
        background-color: #a1ff72;
        margin-right: 10px;
      }
      &.or {
        background-color: #ffa048;
        margin-right: 10px;
      }
      &.minus {
        background-color: #e56f00;
      }
      &:hover {
        border: 1.5px solid #000;
      }
      &.selected {
        border: 1.5px solid #000;
        opacity: 1;
      }
      &.selected {
        font-weight: 600;
      }
    }
  }

  #type_actions {
    opacity: 0;
    transition: all 0.2s ease;
    height: 0;
    position: fixed !important;

    &.show {
      opacity: 1;
      height: initial;
    }

    .total-rows {
      height: 45px;
      min-width: 200px;
      padding: 5px 10px;
      display: flex;
      align-items: center;
      border-radius: 0;
      border: 1px solid #269809;
      background-color: #ecffe6;
      transition: all 0.2s ease;
      float: right;
      margin-right: 10px;
      justify-content: center;
      transition: all 0.2s ease;

      .total-label,
      .total {
        color: #269809;
        font-family: 'Source Sans Pro';
        font-size: 17px;
        display: inline-block;
      }

      .total-label {
        margin-right: 6px;

        i {
          font-size: 14px;
          margin-right: 3px;

          &.loading {
            animation: blink 0.8s ease-in-out infinite;
          }

          @keyframes blink {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0;
            }
          }
        }
      }

      .total {
        font-weight: 600;
        font-size: 21px;
      }

      .spinner-loading {
        display: inline-block;

        .vue-simple-spinner {
          width: 16px !important;
          height: 16px !important;
        }
      }
    }
  }
}

// ── Chat IA ────────────────────────────────────────────────────────────────

// Colors compartits amb el segmentador
$chat-accent: #4a9cb0;       // blau corporatiu (com els spinners i botons del builder)
$chat-accent-dark: #357a8a;
$chat-accent-light: #d4eef3;
$chat-header-bg: #e8e8ee;    // igual que .popup .title
$chat-header-color: #555;    // igual que .popup .title color
$chat-border: #ccc;

.ai-chat-fab {
  position: absolute;
  bottom: 22px;
  right: 22px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: $chat-accent;
  border: none;
  color: #fff;
  font-size: 20px;
  cursor: pointer;
  box-shadow: 0 3px 10px rgba(0,0,0,0.20);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, transform 0.15s ease;
  z-index: 200;

  &:hover {
    background: $chat-accent-dark;
    transform: scale(1.07);
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -6px;
    right: 11px;
    width: 10px;
    height: 10px;
    background: $chat-accent;
    clip-path: polygon(0 0, 100% 0, 100% 100%);
  }

  &:hover::after { background: $chat-accent-dark; }
}

.ai-chat-panel {
  position: absolute;
  bottom: 16px;
  right: 16px;
  width: 340px;
  height: calc(100% - 32px);
  max-height: 560px;
  background: #fff;
  border: 1px solid $chat-border;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  z-index: 200;
  box-shadow: 0 4px 20px rgba(0,0,0,0.13);

  .chat-header {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 8px 11px 7px;
    background: $chat-header-bg;
    color: $chat-header-color;
    font-size: 15px;
    font-weight: 500;
    font-family: 'Source Sans Pro';
    border-bottom: 1px solid $chat-border;
    border-radius: 8px 8px 0 0;

    i.fa-robot { font-size: 18px; color: $chat-accent; }

    span { flex: 1; }

    .chat-close {
      cursor: pointer;
      font-size: 13px;
      opacity: 0.6;
      transition: opacity 0.15s;
      &:hover { opacity: 1; }
    }
  }

  .chat-messages {
    flex: 1;
    overflow-y: auto;
    padding: 14px 12px;
    display: flex;
    flex-direction: column;
    gap: 10px;
    background: #f9f9fb;

    .chat-empty {
      flex: 1;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      text-align: center;
      color: #aaa;
      font-family: 'Source Sans Pro';
      gap: 8px;

      i { font-size: 34px; color: #ccd5da; }
      p { font-size: 13px; margin: 0; line-height: 1.5; color: #888; }
      .chat-example { color: #aab; font-size: 12px; }
    }

    .chat-message {
      display: flex;

      &.user {
        justify-content: flex-end;
        .message-bubble {
          background: $chat-accent;
          color: #fff;
          border-radius: 12px 12px 2px 12px;
        }
      }

      &.assistant {
        justify-content: flex-start;
        .message-bubble {
          background: #fff;
          color: #444;
          border: 1px solid #e0e4e8;
          border-radius: 12px 12px 12px 2px;
        }
      }

      .message-bubble {
        max-width: 85%;
        padding: 7px 11px;
        font-size: 13px;
        font-family: 'Source Sans Pro';
        line-height: 1.5;
        word-break: break-word;

        &.loading {
          display: flex;
          gap: 4px;
          align-items: center;
          padding: 11px 15px;

          .dot {
            width: 6px;
            height: 6px;
            border-radius: 50%;
            background: #aab;
            animation: dot-bounce 1.2s infinite ease-in-out;

            &:nth-child(2) { animation-delay: 0.2s; }
            &:nth-child(3) { animation-delay: 0.4s; }
          }
        }
      }
    }
  }

  .chat-input-area {
    display: flex;
    align-items: flex-end;
    gap: 8px;
    padding: 8px 10px;
    border-top: 1px solid $chat-border;
    background: #f0f0f4;

    textarea {
      flex: 1;
      resize: none;
      border: 1px solid #ccc;
      border-radius: 6px;
      padding: 7px 9px;
      font-family: 'Source Sans Pro';
      font-size: 13px;
      line-height: 1.4;
      min-height: 36px;
      max-height: 110px;
      outline: none;
      transition: border 0.2s ease;
      overflow-y: auto;
      background: #fff;

      &:focus { border-color: $chat-accent; }
      &:disabled { background: #e8e8ee; }
    }

    button {
      width: 34px;
      height: 34px;
      border-radius: 50%;
      background: $chat-accent;
      border: none;
      color: #fff;
      font-size: 13px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      transition: background 0.2s ease;

      &:hover:not(:disabled) { background: $chat-accent-dark; }
      &:disabled { background: #b0cdd4; cursor: default; }
    }
  }
}

.chat-panel-slide-enter-active,
.chat-panel-slide-leave-active {
  transition: transform 0.25s ease, opacity 0.2s ease;
}
.chat-panel-slide-enter-from,
.chat-panel-slide-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

.fab-bounce-enter-active { transition: transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.2s; }
.fab-bounce-leave-active { transition: transform 0.15s ease, opacity 0.15s; }
.fab-bounce-enter-from, .fab-bounce-leave-to { transform: scale(0); opacity: 0; }

@keyframes dot-bounce {
  0%, 80%, 100% { transform: scale(0.7); opacity: 0.5; }
  40% { transform: scale(1); opacity: 1; }
}
</style>
